import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField
} from '@mui/material';
import {
  NavigateBefore,
  NavigateNext,
  Close,
  Image,
  CheckCircle,
  RadioButtonUnchecked,
  Save
} from '@mui/icons-material';
import { clipAudioApi } from '../../../services/api';

function ImagesTable({ 
  images, 
  projectId, 
  onProjectUpdate,
  selectedModel = '',
  modelsLoading = false,
  nbImagesPerScene = 2
}) {
  // Image modal state
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImageData, setSelectedImageData] = useState(null);
  const [regeneratePrompt, setRegeneratePrompt] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [showRegenerateSection, setShowRegenerateSection] = useState(false);
  
  // Generation state for individual scenes
  const [generatingScenes, setGeneratingScenes] = useState({});
  
  // Image selection state - local copy of images with selection state
  const [localImages, setLocalImages] = useState([]);
  const [isSavingSelection, setIsSavingSelection] = useState(false);
  
  // Initialize local images state when images prop changes
  useEffect(() => {
    if (images && images.length > 0) {
      // Create a deep copy of images array with proper structure
      const copiedImages = images.map(imageGroup => ({
        ...imageGroup,
        images: imageGroup.images.map(img => {
          // Handle both string and object formats
          if (typeof img === 'string') {
            return { url: img, selected: false };
          }
          return { ...img, selected: img.selected || false };
        })
      }));
      setLocalImages(copiedImages);
    }
  }, [images]);

  // Image modal functions
  const handleImageClick = (imagesArray, imageIndex = 0, imageData = null) => {
    // Convert images array to URL array for modal display
    const imageUrls = imagesArray.map(img => {
      return typeof img === 'string' ? img : img.url;
    });
    setSelectedImages(imageUrls);
    setCurrentImageIndex(imageIndex);
    setSelectedImageData(imageData); // Contains the full imageGroup with scene info
    setRegeneratePrompt('');
    setImageModalOpen(true);
  };

  // Toggle image selection from modal
  const handleToggleSelectionFromModal = () => {
    if (!selectedImageData) return;
    
    // Find the scene index in localImages
    const sceneIndex = localImages.findIndex(group => 
      group.scene_id === selectedImageData.scene_id || 
      group.scene?.number === selectedImageData.scene?.number
    );
    
    if (sceneIndex === -1) return;
    
    // Toggle selection for the current image in the modal
    setLocalImages(prev => {
      const updated = [...prev];
      const imageGroup = { ...updated[sceneIndex] };
      imageGroup.images = [...imageGroup.images];
      
      // Find the image that matches the current URL
      const imageIndex = imageGroup.images.findIndex(img => {
        const imgUrl = typeof img === 'string' ? img : img.url;
        return imgUrl === selectedImages[currentImageIndex];
      });
      
      if (imageIndex !== -1) {
        const imageObj = typeof imageGroup.images[imageIndex] === 'string' 
          ? { url: imageGroup.images[imageIndex], selected: false }
          : imageGroup.images[imageIndex];
        
        imageGroup.images[imageIndex] = {
          ...imageObj,
          selected: !imageObj.selected
        };
        updated[sceneIndex] = imageGroup;
      }
      
      return updated;
    });
  };

  // Get current image selection status in modal
  const isCurrentImageSelected = () => {
    if (!selectedImageData || !selectedImages[currentImageIndex]) return false;
    
    const sceneIndex = localImages.findIndex(group => 
      group.scene_id === selectedImageData.scene_id || 
      group.scene?.number === selectedImageData.scene?.number
    );
    
    if (sceneIndex === -1) return false;
    
    const imageGroup = localImages[sceneIndex];
    const currentImageUrl = selectedImages[currentImageIndex];
    
    const image = imageGroup.images.find(img => {
      const imgUrl = typeof img === 'string' ? img : img.url;
      return imgUrl === currentImageUrl;
    });
    
    if (!image) return false;
    const imageObj = typeof image === 'string' ? { url: image, selected: false } : image;
    return imageObj.selected || false;
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImages([]);
    setCurrentImageIndex(0);
    setSelectedImageData(null);
    setRegeneratePrompt('');
    setIsRegenerating(false);
    setShowRegenerateSection(false);
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : selectedImages.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < selectedImages.length - 1 ? prev + 1 : 0));
  };

  // Image regeneration function
  const handleRegenerateImage = async () => {
    if (!regeneratePrompt.trim() || !selectedImages[currentImageIndex] || !selectedImageData) return;

    setIsRegenerating(true);
    
    try {
      // selectedImages is now an array of URLs (converted from image objects)
      const imageUrl = selectedImages[currentImageIndex];
      const result = await clipAudioApi.updateImageFromDetails(projectId, imageUrl, selectedImageData.scene.number, regeneratePrompt);
      console.log('Image regeneration successful:', result);
      // Refresh project data to get latest state
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
      // Close modal
      handleCloseImageModal();
    } catch (error) {
      console.error('Error regenerating image:', error);
    } finally {
      setIsRegenerating(false);
    }
  };

  // Image resize function
  const handleResizeImage = async () => {
    if (!selectedImages[currentImageIndex] || !selectedImageData) return;

    setIsResizing(true);
    try {
      // selectedImages is now an array of URLs (converted from image objects)
      const imageUrl = selectedImages[currentImageIndex];
      const result = await clipAudioApi.resizeImage(projectId, imageUrl, selectedImageData.scene.number);
      console.log('Image resize successful:', result);
      
      // Refresh project data to get latest state
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      console.error('Error resizing image:', error);
      alert('Erreur lors du redimensionnement de l\'image');
    } finally {
      setIsResizing(false);
    }
  };

  // Toggle image selection
  const handleToggleImageSelection = (sceneIndex, imageIndex, event) => {
    event.stopPropagation(); // Prevent triggering modal
    
    setLocalImages(prev => {
      const updated = [...prev];
      const imageGroup = { ...updated[sceneIndex] };
      imageGroup.images = [...imageGroup.images];
      imageGroup.images[imageIndex] = {
        ...imageGroup.images[imageIndex],
        selected: !imageGroup.images[imageIndex].selected
      };
      updated[sceneIndex] = imageGroup;
      return updated;
    });
  };

  // Save image selections to backend
  const handleSaveImageSelections = async () => {
    setIsSavingSelection(true);
    
    try {
      // Send the entire images array with updated selected flags
      const result = await clipAudioApi.updateImagesSelection(projectId, localImages);
      console.log('Images selection updated successfully:', result);
      
      // Refresh project data to get latest state
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      console.error('Error saving image selections:', error);
      alert('Erreur lors de la sauvegarde de la sélection des images');
    } finally {
      setIsSavingSelection(false);
    }
  };

  // Generate images for a specific scene
  const handleGenerateSceneImages = async (sceneId) => {
    setGeneratingScenes(prev => ({ ...prev, [sceneId]: true }));
    
    try {
      // Include model selection, scene_id, and number of images per scene in stepData
      const stepData = {
        model: selectedModel || "google/gemini-2.5-flash-image",
        scene_id: sceneId,
        nb_images_per_scene: typeof nbImagesPerScene === 'number' ? nbImagesPerScene : 2
      };
      const result = await clipAudioApi.executeStep(projectId, 3, stepData);
      console.log(`Scene ${sceneId} image generation successful:`, result);
      
      // Refresh project data to get latest state
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      console.error(`Error generating images for scene ${sceneId}:`, error);
    } finally {
      setGeneratingScenes(prev => ({ ...prev, [sceneId]: false }));
    }
  };

  if (!images || images.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
        Aucune image générée pour le moment
      </Typography>
    );
  }

  // Get the images to display (use localImages if available, otherwise fallback to images prop)
  const displayImages = localImages.length > 0 ? localImages : images;
  
  // Count total selected images
  const totalSelected = displayImages.reduce((total, group) => {
    return total + (group.images || []).filter(img => {
      const imageObj = typeof img === 'string' ? { url: img, selected: false } : img;
      return imageObj.selected;
    }).length;
  }, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#2C3E50', fontWeight: 'bold' }}>
          🖼️ Images générées ({displayImages.length} scènes)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
            {totalSelected} image{totalSelected > 1 ? 's' : ''} sélectionnée{totalSelected > 1 ? 's' : ''}
          </Typography>
          <Button
            variant="contained"
            onClick={handleSaveImageSelections}
            disabled={isSavingSelection}
            startIcon={isSavingSelection ? <CircularProgress size={16} /> : <Save />}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
              borderRadius: '20px',
              px: 2,
              py: 0.5,
              fontSize: '0.8rem',
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none'
              }
            }}
          >
            {isSavingSelection ? 'Sauvegarde...' : 'Sauvegarder sélection'}
          </Button>
        </Box>
      </Box>
      
      {/* Table for lyric_line, scene description, images, and generate button */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8F9FA' }}>
              <TableCell sx={{ fontWeight: 'bold', color: '#2C3E50', fontSize: '1rem' }}>
                🎵 Ligne de paroles
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#2C3E50', fontSize: '1rem' }}>
                📝 Description de la scène
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#2C3E50', fontSize: '1rem' }}>
                🖼️ Images ({displayImages.reduce((total, group) => total + (group.images?.length || 0), 0)}) - Cliquez pour voir, cochez pour sélectionner
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#2C3E50', fontSize: '1rem', textAlign: 'center' }}>
                ⚡ Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayImages.map((imageGroup, sceneIndex) => (
              <TableRow key={sceneIndex} sx={{ '&:nth-of-type(odd)': { bgcolor: '#FAFAFA' } }}>
                <TableCell sx={{ 
                  fontWeight: 'bold', 
                  color: '#E65100',
                  fontSize: '0.95rem',
                  verticalAlign: 'top',
                  width: '25%'
                }}>
                  {imageGroup.scene.lyric_line || 'N/A'}
                </TableCell>
                <TableCell sx={{ 
                  color: '#2C3E50',
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                  verticalAlign: 'top',
                  width: '35%'
                }}>
                  {imageGroup.scene.one_line}
                </TableCell>
                <TableCell sx={{ 
                  verticalAlign: 'top',
                  width: '25%'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1,
                    justifyContent: 'center'
                  }}>
                    {imageGroup.images.map((image, imgIndex) => {
                      // Ensure image is an object with url and selected
                      const imageObj = typeof image === 'string' ? { url: image, selected: false } : image;
                      const isSelected = imageObj.selected || false;
                      
                      return (
                        <Box key={imgIndex} sx={{
                          position: 'relative',
                          borderRadius: 1,
                          overflow: 'visible',
                          border: isSelected ? '3px solid #27AE60' : '2px solid #E1E8ED',
                          cursor: 'pointer',
                          width: '80px',
                          height: '80px',
                          bgcolor: isSelected ? '#E8F8F5' : 'transparent',
                          '&:hover': {
                            borderColor: isSelected ? '#229954' : '#3498DB',
                            transform: 'scale(1.05)',
                            transition: 'all 0.3s ease'
                          }
                        }}>
                          {/* Image */}
                          <Box
                            onClick={() => handleImageClick(imageGroup.images, imgIndex, imageGroup)}
                            sx={{
                              width: '100%',
                              height: '100%',
                              position: 'relative',
                              overflow: 'hidden',
                              borderRadius: '4px'
                            }}
                          >
                            <img
                              src={imageObj.url}
                              alt={`${imageGroup.scene.title} - Image ${imgIndex + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <Box sx={{
                              display: 'none',
                              width: '100%',
                              height: '100%',
                              bgcolor: '#F5F5F5',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#999'
                            }}>
                              <Typography variant="caption">Image non disponible</Typography>
                            </Box>
                          </Box>
                          
                          {/* Selection Checkbox */}
                          <IconButton
                            onClick={(e) => handleToggleImageSelection(sceneIndex, imgIndex, e)}
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: 'white',
                              width: '24px',
                              height: '24px',
                              padding: 0,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              '&:hover': {
                                bgcolor: '#F5F5F5',
                                transform: 'scale(1.1)'
                              }
                            }}
                          >
                            {isSelected ? (
                              <CheckCircle sx={{ color: '#27AE60', fontSize: '24px' }} />
                            ) : (
                              <RadioButtonUnchecked sx={{ color: '#95A5A6', fontSize: '24px' }} />
                            )}
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                </TableCell>
                <TableCell sx={{ 
                  verticalAlign: 'top',
                  width: '15%',
                  textAlign: 'center'
                }}>
                  <Button
                    variant="contained"
                    onClick={() => handleGenerateSceneImages(imageGroup.scene.number)}
                    disabled={generatingScenes[imageGroup.scene.number] || modelsLoading}
                    startIcon={generatingScenes[imageGroup.scene.number] ? <CircularProgress size={16} /> : <Image />}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                      borderRadius: '20px',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      minWidth: '100px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)',
                        transform: 'translateY(-1px)'
                      },
                      '&:disabled': {
                        background: '#BDBDBD',
                        transform: 'none'
                      }
                    }}
                  >
                    {generatingScenes[imageGroup.scene.number] ? 'Génération...' : 'Générer'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Image Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#FFFFFF',
            maxHeight: showRegenerateSection ? '95vh' : '85vh',
            height: showRegenerateSection ? 'auto' : '85vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 1
        }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 'bold',
            color: '#2C3E50',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Image color="secondary" />
            Images de la scène ({selectedImages.length} image{selectedImages.length > 1 ? 's' : ''})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton
              onClick={handleToggleSelectionFromModal}
              sx={{
                bgcolor: isCurrentImageSelected() ? '#E8F8F5' : 'transparent',
                border: '2px solid',
                borderColor: isCurrentImageSelected() ? '#27AE60' : '#95A5A6',
                color: isCurrentImageSelected() ? '#27AE60' : '#95A5A6',
                width: '36px',
                height: '36px',
                '&:hover': {
                  bgcolor: isCurrentImageSelected() ? '#D5F4E6' : '#F5F5F5',
                  borderColor: isCurrentImageSelected() ? '#229954' : '#7F8C8D',
                  transform: 'scale(1.05)'
                }
              }}
              title={isCurrentImageSelected() ? 'Désélectionner cette image' : 'Sélectionner cette image'}
            >
              {isCurrentImageSelected() ? (
                <CheckCircle sx={{ fontSize: '24px' }} />
              ) : (
                <RadioButtonUnchecked sx={{ fontSize: '24px' }} />
              )}
            </IconButton>
            <Button
              onClick={() => setShowRegenerateSection(!showRegenerateSection)}
              variant="outlined"
              size="small"
              startIcon={<Image />}
              sx={{
                borderColor: '#9C27B0',
                color: '#9C27B0',
                borderRadius: '20px',
                px: 2,
                py: 0.5,
                fontSize: '0.8rem',
                fontWeight: 'bold',
                '&:hover': {
                  borderColor: '#7B1FA2',
                  backgroundColor: '#F3E5F5',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {showRegenerateSection ? 'Masquer' : 'Régénérer'}
            </Button>
            <Button
              onClick={handleResizeImage}
              variant="outlined"
              size="small"
              disabled={isResizing || !selectedImages[currentImageIndex] || !selectedImageData}
              startIcon={isResizing ? <CircularProgress size={16} /> : <Image />}
              sx={{
                borderColor: '#FF9800',
                color: '#FF9800',
                borderRadius: '20px',
                px: 2,
                py: 0.5,
                fontSize: '0.8rem',
                fontWeight: 'bold',
                '&:hover': {
                  borderColor: '#F57C00',
                  backgroundColor: '#FFF3E0',
                  transform: 'translateY(-1px)'
                },
                '&:disabled': {
                  borderColor: '#E0E0E0',
                  color: '#BDBDBD',
                  backgroundColor: '#F5F5F5'
                }
              }}
            >
              {isResizing ? 'Redimensionnement...' : 'Reformatage 16:9'}
            </Button>
            <IconButton
              onClick={handleCloseImageModal}
              sx={{ 
                color: '#7F8C8D',
                '&:hover': {
                  backgroundColor: '#F5F5F5'
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          {selectedImages.length > 0 && (
            <Box sx={{ position: 'relative' }}>
              {/* Main Image Display */}
              <Box sx={{ 
                position: 'relative',
                maxWidth: '100%',
                maxHeight: showRegenerateSection ? '40vh' : '60vh',
                height: showRegenerateSection ? 'auto' : '60vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img
                  src={selectedImages[currentImageIndex]}
                  alt={`Image ${currentImageIndex + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: showRegenerateSection ? '40vh' : '60vh',
                    height: showRegenerateSection ? 'auto' : '60vh',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <Box sx={{
                  display: 'none',
                  width: '100%',
                  height: showRegenerateSection ? '40vh' : '60vh',
                  bgcolor: '#F5F5F5',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  borderRadius: '8px'
                }}>
                  <Typography variant="h6">Image non disponible</Typography>
                </Box>
              </Box>

              {/* Navigation Arrows */}
              {selectedImages.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePreviousImage}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 1)',
                        transform: 'translateY(-50%) scale(1.1)'
                      }
                    }}
                  >
                    <NavigateBefore />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 1)',
                        transform: 'translateY(-50%) scale(1.1)'
                      }
                    }}
                  >
                    <NavigateNext />
                  </IconButton>
                </>
              )}

              {/* Image Counter */}
              <Typography variant="body1" sx={{ 
                mt: 1.5,
                color: '#7F8C8D',
                fontWeight: 'bold',
                textAlign: 'center'
              }}>
                {currentImageIndex + 1} / {selectedImages.length}
              </Typography>

              {/* Regeneration Section */}
              {showRegenerateSection && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F8F9FA', borderRadius: 2, border: '1px solid #E1E8ED' }}>
                  <Typography variant="h6" sx={{ 
                    mb: 2, 
                    color: '#2C3E50', 
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Image color="secondary" />
                    Régénérer cette image
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Décrivez la nouvelle image que vous souhaitez générer"
                    placeholder="Ex: Même scène mais avec un éclairage plus dramatique et des couleurs plus vives..."
                    value={regeneratePrompt}
                    onChange={(e) => setRegeneratePrompt(e.target.value)}
                    multiline
                    rows={2}
                    variant="outlined"
                    sx={{ mb: 1.5 }}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      onClick={() => setRegeneratePrompt('')}
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: '#95A5A6',
                        color: '#7F8C8D',
                        '&:hover': {
                          borderColor: '#7F8C8D',
                          backgroundColor: '#F8F9FA'
                        }
                      }}
                    >
                      Effacer
                    </Button>
                    <Button
                      onClick={handleRegenerateImage}
                      disabled={!regeneratePrompt.trim() || isRegenerating}
                      variant="contained"
                      startIcon={isRegenerating ? <CircularProgress size={16} /> : <Image />}
                      sx={{
                        background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
                        borderRadius: '20px',
                        px: 3,
                        py: 1,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #7B1FA2 0%, #6A1B9A 100%)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(156, 39, 176, 0.4)'
                        },
                        '&:disabled': {
                          background: '#BDBDBD',
                          transform: 'none',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      {isRegenerating ? 'Génération...' : 'Générer une nouvelle image'}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Thumbnail Navigation */}
              {selectedImages.length > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  justifyContent: 'center', 
                  mt: 1.5,
                  flexWrap: 'wrap'
                }}>
                  {selectedImages.map((imageUrl, index) => {
                    // Check if this thumbnail image is selected
                    const isThumbnailSelected = (() => {
                      if (!selectedImageData) return false;
                      const sceneIndex = localImages.findIndex(group => 
                        group.scene_id === selectedImageData.scene_id || 
                        group.scene?.number === selectedImageData.scene?.number
                      );
                      if (sceneIndex === -1) return false;
                      const imageGroup = localImages[sceneIndex];
                      const image = imageGroup.images.find(img => {
                        const imgUrl = typeof img === 'string' ? img : img.url;
                        return imgUrl === imageUrl;
                      });
                      if (!image) return false;
                      const imageObj = typeof image === 'string' ? { url: image, selected: false } : image;
                      return imageObj.selected || false;
                    })();
                    
                    return (
                      <Box
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        sx={{
                          position: 'relative',
                          width: '60px',
                          height: '60px',
                          borderRadius: 1,
                          overflow: 'visible',
                          border: currentImageIndex === index 
                            ? '3px solid #3498DB' 
                            : isThumbnailSelected 
                              ? '2px solid #27AE60' 
                              : '2px solid #E1E8ED',
                          bgcolor: isThumbnailSelected ? '#E8F8F5' : 'transparent',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#3498DB',
                            transform: 'scale(1.05)',
                            transition: 'all 0.3s ease'
                          }
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={`Thumbnail ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            borderRadius: '4px'
                          }}
                        />
                        {isThumbnailSelected && (
                          <CheckCircle 
                            sx={{ 
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              color: '#27AE60',
                              fontSize: '20px',
                              bgcolor: 'white',
                              borderRadius: '50%'
                            }} 
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default ImagesTable;
